import { keepPreviousData, useQueries, useQuery } from '@tanstack/react-query';

import { execute } from '@/graphql/execute';
import type { AnalysisRunDetailsQuery, AnalysisRunInputQuery } from '@/graphql/graphql';
import { analysisRunDetailsQuery, analysisRunInputQuery } from '@/queries/analysisRuns.query';

import { isRunActive, type AnalysisRunItem } from './runs';
import { ACTIVE_RUN_POLL_MS } from './useAnalysisRuns';

export const analysisRunDetailsQueryKey = (batchId: string, status: string) =>
  ['analysisRunDetails', batchId, status] as const;

type DetailsRun = Pick<AnalysisRunItem, 'batchId' | 'status' | 'createdAt'>;

const detailsOptions = (run: DetailsRun | null, open: boolean) => {
  const batchId = run?.batchId ?? '';
  return {
    queryKey: analysisRunDetailsQueryKey(batchId, run?.status ?? ''),
    queryFn: () => execute(analysisRunDetailsQuery, { id: batchId }),
    enabled: open && !!batchId,
    select: (data: AnalysisRunDetailsQuery) => data.batch,
    placeholderData: keepPreviousData,
    refetchInterval: () => (run && isRunActive(run, Date.now()) ? ACTIVE_RUN_POLL_MS : false),
  } as const;
};

/**
 * The open run's batch + jobs for the details drawer (slim
 * `AnalysisRunDetails`, never `getBatch`).
 *
 * Enabled only while the drawer shows this run. Polls through
 * `refetchInterval` at 2 s only while the run is active (`isRunActive`) and
 * not at all otherwise — never a manual `refetch()` loop. The run's derived
 * status is part of the key, so the run finishing (seen by the list's own
 * poll) fetches the final steps once; `keepPreviousData` keeps the drawer
 * filled across that key change.
 */
export function useAnalysisRunDetails(run: DetailsRun | null, open: boolean) {
  return useQuery(detailsOptions(run, open));
}

/** `useAnalysisRunDetails` for each part of a paired comparison, in order —
 *  same keys, same poll rules. */
export function useAnalysisRunsDetails(runs: readonly DetailsRun[], open: boolean) {
  return useQueries({ queries: runs.map((run) => detailsOptions(run, open)) });
}

const paramsOptions = (jobId: string | null, open: boolean) =>
  ({
    queryKey: ['analysisRunInput', jobId ?? ''],
    queryFn: () => execute(analysisRunInputQuery, { jobId: jobId! }),
    enabled: open && !!jobId,
    select: (data: AnalysisRunInputQuery) => data.analysisRunInput as unknown,
    staleTime: Infinity,
  }) as const;

/**
 * The run's root-job `inputJson` for the Parameters section: read once per
 * opened run and never polled. Same key and query as Re-run's lazy fetch
 * (`useOpenAnalysisRerun`), so either warms the other.
 */
export function useAnalysisRunParams(jobId: string | null, open: boolean) {
  return useQuery(paramsOptions(jobId, open));
}

/** `useAnalysisRunParams` for each part of a paired comparison, in order. */
export function useAnalysisRunsParams(jobIds: readonly string[], open: boolean) {
  return useQueries({ queries: jobIds.map((jobId) => paramsOptions(jobId, open)) });
}
